import type { DbError, DbResult } from '@/lib/db-tenant'
import type { TenantSupabaseClient } from '@/lib/supabase-tenant'
import { nextCalendarDay } from '@/lib/date-utils'
import type { ProjectionRecord } from '@/lib/projection-cell'
import { fetchAllRows } from '@/lib/fetch-all-rows'

/**
 * DCP-406: ids per `.in('id', …)` request. The filter goes into the request
 * URL, and a UUID with its separator is ~37 characters, so 100 ids add ~3.7 KB
 * — comfortably below the 8 KB URL/header limits common in proxies and
 * gateways, even next to the rest of the query string. A batch create or a
 * paste of a large selection would otherwise put every id in one URL.
 */
export const PROJECTION_ID_CHUNK = 100

/** One Cash Position cell; `start`/`end` inclusive ISO dates. */
export interface ProjectionCellQuery {
  /** The cell's facility, or a pack's members. */
  facilityIds: readonly string[]
  start: string
  end: string
  categoryIds: readonly string[]
}

/**
 * A multi-request write: `updated` counts rows the database confirmed;
 * `firstError` is the first failed request's (later requests still run).
 */
export interface BulkWriteOutcome {
  updated: number
  requested: number
  firstError: DbError | null
}

/** Data access of the "Manage projections" modal; single-row CRUD stays on `tenantDb.projections`. */
export function projectionCellDb(client: TenantSupabaseClient) {
  /** Sets `date` on `ids` in chunks; with `onlyIfDateIs`, only on rows still dated that. */
  async function writeDate(
    ids: readonly string[],
    date: string,
    onlyIfDateIs: string | null,
  ): Promise<BulkWriteOutcome> {
    let updated = 0
    let firstError: DbError | null = null
    for (let i = 0; i < ids.length; i += PROJECTION_ID_CHUNK) {
      let update = client
        .from('projections')
        .update({ date })
        .in('id', ids.slice(i, i + PROJECTION_ID_CHUNK))
      if (onlyIfDateIs !== null) update = update.eq('date', onlyIfDateIs)
      const { data, error } = await update.select('id')
      if (error) firstError ??= { message: error.message, code: error.code }
      else updated += data.length
    }
    return { updated, requested: ids.length, firstError }
  }

  return {
    /** Every projection the cell counts, by the report's predicate. No facility or category ids → no request. */
    async list(query: ProjectionCellQuery): Promise<DbResult<ProjectionRecord[]>> {
      if (query.facilityIds.length === 0 || query.categoryIds.length === 0) return { data: [], error: null }
      return fetchAllRows((from, to) =>
        client
          .from('projections')
          .select('*')
          // Not chunked: a pack has tens of members at most (~37 B each, far
          // below the URL limit above), and a split read would need merged pages.
          .in('facility_id', [...query.facilityIds])
          .eq('include_in_report', true)
          .gte('date', query.start)
          .lte('date', query.end)
          .in('category_id', [...query.categoryIds])
          // Unique tiebreak so pages neither skip nor repeat rows.
          .order('date', { ascending: true })
          .order('id', { ascending: true })
          .range(from, to),
      )
    },

    /** Sets one absolute date on every projection in `ids`. */
    setDate(ids: readonly string[], date: string): Promise<BulkWriteOutcome> {
      return writeDate(ids, date, null)
    },

    /**
     * Moves each projection one calendar day later, one update per distinct date.
     * Each update is guarded by the date the caller saw: a row re-dated elsewhere
     * meanwhile is skipped (not counted) rather than moved from a stale value.
     */
    async moveToNextDay(rows: ReadonlyArray<Pick<ProjectionRecord, 'id' | 'date'>>): Promise<BulkWriteOutcome> {
      const idsByDate = new Map<string, string[]>()
      for (const row of rows) {
        const ids = idsByDate.get(row.date)
        if (ids) ids.push(row.id)
        else idsByDate.set(row.date, [row.id])
      }
      let updated = 0
      let firstError: DbError | null = null
      for (const [date, ids] of idsByDate) {
        const outcome = await writeDate(ids, nextCalendarDay(date), date)
        updated += outcome.updated
        firstError ??= outcome.firstError
      }
      return { updated, requested: rows.length, firstError }
    },
  }
}
